// The documents the Wallet offers, by category, and which of them a given user sees. The Wallet
// page lists these and the API server checks purchases and verifications against the same list, so
// the two can never offer different documents under the same id.
//
// Switches are passed in as arguments; nothing here reads the environment. Written as plain
// CommonJS so the API server, which runs its files under plain node, can require this same file
// the browser bundle imports.

const { WORK_DOCUMENTS } = require('./workDocumentRules');

const CITIZENSHIP_CERTIFICATE_NAMES = [
  WORK_DOCUMENTS.naturalization_certificate,
  WORK_DOCUMENTS.citizenship_certificate,
];

// Categories whose items are checked by contacting a referee, never by the identity provider.
const CONTACT_CATEGORIES = ['education', 'experience'];

// Categories not built yet, so offered to no one. Their items are still built below, and what is
// stored for an item someone already bought is left alone; offering one again is removing it here.
const UNBUILT_CATEGORIES = Object.freeze(['experience', 'education', 'insurance', 'pastProjects']);

const WALLET_CATEGORIES = Object.freeze({
  // Mandatory identity documents (Driver's License or State ID - one required)
  mandatoryIdentity: [
    {
      id: 'identity-1',
      name: "Driver's license",
      price: 90,
      description: 'Must contain photo or identifying info',
      isMandatory: true,
    },
    {
      id: 'identity-2',
      name: 'State ID',
      price: 90,
      description: 'Must contain photo or identifying info',
      isMandatory: true,
    },
  ],
  // Foreign Passport, offered (and mandatory) only to non-US citizens
  foreignPassport: [
    {
      id: 'identity-13',
      name: 'Foreign Passport',
      price: 90,
      description: 'Valid and unexpired',
      isMandatory: true,
    },
  ],
  // Optional identity documents
  identity: [
    {
      id: 'both-1',
      name: 'U.S. Passport',
      price: 90,
      description: 'Valid and unexpired',
    },
    {
      id: 'both-1-ppc',
      name: 'U.S. Passport Card',
      price: 90,
      description: 'Valid and unexpired',
    },
    {
      id: 'both-2',
      name: 'Permanent Resident Card (Form I-551)',
      price: 90,
      description: 'Green Card; current and older designs accepted',
    },
    {
      id: 'both-4',
      name: 'Employment Authorization Document (Form I-766)',
      price: 90,
      description: 'Employment Authorization Document (EAD)',
    },
    {
      id: 'both-5',
      name: 'Passport from FSM or RMI',
      price: 90,
      description: 'FSM or RMI',
    },
    // {
    //   id: 'identity-4',
    //   name: 'Government-issued ID card (federal, state, local)',
    //   price: 90,
    //   description: 'Includes municipal IDs, government agencies',
    // },
    {
      id: 'identity-5',
      name: 'School ID card',
      price: 90,
      description: 'Issued by accredited U.S. school',
    },
    // {
    //   id: 'identity-6',
    //   name: "Voter's registration card",
    //   price: 90,
    //   description: 'Official voter card',
    // },
    {
      id: 'identity-7',
      name: 'U.S. Military card or draft record',
      price: 90,
      description: 'Issued by U.S. Armed Forces',
    },
    {
      id: 'identity-8',
      name: "Military dependent's ID card",
      price: 90,
      description: 'For dependents of military personnel',
    },
    {
      id: 'identity-9',
      name: 'U.S. Coast Guard Merchant Mariner Card',
      price: 90,
      description: 'Merchant Marine ID',
    },
    // {
    //   id: 'identity-10',
    //   name: 'Native American tribal document',
    //   price: 90,
    //   description: 'Federally recognized tribes',
    // },
    // {
    //   id: 'identity-11',
    //   name: "Driver's license from Canadian authority",
    //   price: 90,
    //   description: 'Issued by Canadian province or territory',
    // },
    {
      id: 'identity-12',
      name: 'Under 18 alternatives (school, clinic, or daycare records)',
      price: 90,
      description: 'For minors with no other docs available',
    },
    {
      id: 'identity-14',
      name: 'U.S. Military Card / Draft Record',
      price: 90,
      description: '',
    },
  ],
  work: [
    {
      id: 'both-1',
      name: 'U.S. Passport',
      price: 90,
      description: 'Valid and unexpired',
    },
    {
      id: 'both-1-ppc',
      name: 'U.S. Passport Card',
      price: 90,
      description: 'Valid and unexpired',
    },
    {
      id: 'both-2',
      name: 'Permanent Resident Card (Form I-551)',
      price: 90,
      description: 'Green Card; current and older designs accepted',
    },
    {
      id: 'both-6',
      name: 'Form I-94',
      price: 90,
      description: 'I-94 must show status authorizing employment',
    },
    {
      id: 'both-4',
      name: 'Employment Authorization Document (Form I-766)',
      price: 90,
      description: 'Employment Authorization Document (EAD)',
    },
    {
      id: 'work-1',
      name: 'Social Security Card (unrestricted)',
      price: 90,
      description: "Not marked 'NOT VALID FOR EMPLOYMENT'",
    },
    // {
    //   id: 'work-8',
    //   name: 'Form I-94',
    //   price: 90,
    //   description: 'I-94 must show status authorizing employment',
    // },
    {
      id: 'work-2',
      name: 'Certification of Birth Abroad (Forms DS-1350, FS-545, FS-240)',
      price: 90,
      description: 'Issued abroad by U.S. Department of State',
    },
    {
      id: 'work-3',
      name: 'U.S. Birth Certificate (certified copy)',
      price: 90,
      description: 'Certified by state or territory',
    },
    // {
    //   id: 'work-4',
    //   name: 'Native American tribal document',
    //   price: 90,
    //   description: 'Federally recognized tribe',
    // },
    // {
    //   id: 'work-5',
    //   name: 'Form I-197 (U.S. Citizen Identification Card)',
    //   price: 90,
    //   description: 'Issued by DHS',
    // },
    // {
    //   id: 'work-6',
    //   name: 'Form I-179 (ID card for resident citizen)',
    //   price: 90,
    //   description: 'Issued by DHS',
    // },
    // {
    //   id: 'work-7',
    //   name: 'Employment Authorization Document issued by DHS (various visa types)',
    //   price: 90,
    //   description: 'Covers F-1, J-1, M-1, Asylees, Refugees, TPS, Parolees, H-1B, etc.',
    // },
    {
      id: 'work-9',
      name: 'Form I-20',
      price: 90,
      description: '',
    },
    {
      id: 'work-10',
      name: WORK_DOCUMENTS.naturalization_certificate,
      price: 90,
      description: 'Issued by USCIS to naturalized citizens',
    },
    {
      id: 'work-11',
      name: WORK_DOCUMENTS.citizenship_certificate,
      price: 90,
      description: 'Issued by USCIS for derived or acquired citizenship',
    },
  ],
  experience: [
    { id: 'experience-1', name: 'Experience Certificate', price: 90 },
    { id: 'experience-2', name: 'Reference Letter', price: 90 },
    { id: 'experience-3', name: 'Portfolio Verification', price: 90 },
  ],
  education: [
    { id: 'education-1', name: 'Degree Certificate', price: 90 },
    { id: 'education-2', name: 'Transcript', price: 90 },
    { id: 'education-3', name: 'Academic Records', price: 90 },
  ],
  // KYB categories for agencies and customers
  businessRegistry: [
    {
      id: 'kyb-1',
      name: 'Business Registry Verification (KYB)',
      price: 90,
      description:
        'Complete business verification including registration, tax ID, and business structure',
      isMandatory: true,
      isKYB: true,
    },
  ],
});

// A company list stored on the listing, as an array or as its JSON text. Anything unreadable is no
// list at all.
const listOf = (value, label) => {
  if (!value) return [];
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(`Error parsing ${label} data:`, error);
    return [];
  }
};

const beneficialOwnerItems = publicData =>
  listOf(publicData.beneficialOwners, 'beneficial owners').map((owner, index) => ({
    id: `beneficial-owner-${index}`,
    name:
      `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || `Owner ${index + 1}`,
    description: `${owner.title || 'Beneficial Owner'} - ${owner.ownershipPercentage ||
      0}% ownership`,
    email: owner.email || '',
    phone: owner.phone || '',
    phoneNumber: owner.phoneNumber || '',
    title: owner.title || '',
    ownershipPercentage: owner.ownershipPercentage || 0,
    price: 90,
    category: 'beneficialOwner',
  }));

const insuranceItems = publicData =>
  listOf(publicData.insurance, 'insurance').map((ins, index) => ({
    id: `insurance-${index}`,
    name: ins.type || `Insurance ${index + 1}`,
    description: `${ins.provider || 'Provider'} - Policy: ${ins.policyNumber || 'N/A'}`,
    provider: ins.provider || '',
    policyNumber: ins.policyNumber || '',
    expirationDate: ins.expirationDate ? new Date(ins.expirationDate).toDateString() : '',
    coverageAmount: ins.coverageAmount || '',
    price: 90,
    category: 'insurance',
  }));

const pastProjectItems = publicData =>
  listOf(publicData.pastProjects, 'past projects').map((project, index) => ({
    id: `past-project-${index}`,
    name: project.projectName || `Project ${index + 1}`,
    description: `${project.clientName || 'Client'} - ${project.projectType ||
      'Project'}`,
    clientName: project.clientName || '',
    projectType: project.projectType || '',
    startDate: project.startDate ? new Date(project.startDate).toDateString() : '',
    endDate: project.endDate ? new Date(project.endDate).toDateString() : '',
    projectValue: project.projectValue || '',
    price: 90,
    category: 'pastProjects',
  }));

const copyItems = items => items.map(item => Object.assign({}, item));

/**
 * The Wallet documents a user sees, by category and as one list.
 *
 * Candidates see the identity and work documents. Agencies and customers see the business
 * verification and one item per beneficial owner on their listing. Both see the driver's license or
 * state ID, and the foreign passport when they are not US citizens. The citizenship certificates are
 * offered only while the additional work documents are on. Experience, education, insurance and past
 * projects are not offered until they are built: their keys are absent from `categories`.
 *
 * @param {Object} args
 * @param {'candidate'|'agency'|'customer'} args.userType
 * @param {boolean} args.isUsCitizen
 * @param {boolean} args.additionalDocuments - whether the additional work documents are offered
 * @param {Object} [args.subjectPublicData] - the company listing's public data, for its own items
 * @returns {{ categories: Object<string, Object[]>, items: Object[] }} every item carries its
 *   `category` and `subject` ('self' for a candidate's own documents, 'company' otherwise); `items`
 *   lists each id once, in category order
 */
const walletCatalogFor = ({ userType, isUsCitizen, additionalDocuments, subjectPublicData } = {}) => {
  const isAgency = userType === 'agency';
  const isCustomer = userType === 'customer';
  const isCompany = isAgency || isCustomer;
  const subject = isCompany ? 'company' : 'self';
  const publicData = subjectPublicData || {};

  const categories = {
    mandatoryIdentity: copyItems(WALLET_CATEGORIES.mandatoryIdentity),
    foreignPassport: !isUsCitizen ? copyItems(WALLET_CATEGORIES.foreignPassport) : [],
  };

  if (isCompany) {
    categories.businessRegistry = copyItems(WALLET_CATEGORIES.businessRegistry);
    categories.beneficialOwner = beneficialOwnerItems(publicData);
    if (isAgency) {
      categories.insurance = insuranceItems(publicData);
      categories.pastProjects = pastProjectItems(publicData);
    }
  } else {
    categories.identity = copyItems(WALLET_CATEGORIES.identity);
    categories.work = copyItems(WALLET_CATEGORIES.work);
    categories.experience = copyItems(WALLET_CATEGORIES.experience);
    categories.education = copyItems(WALLET_CATEGORIES.education);

    if (!additionalDocuments) {
      categories.work = categories.work.filter(item => !CITIZENSHIP_CERTIFICATE_NAMES.includes(item.name));
    }
  }

  UNBUILT_CATEGORIES.forEach(name => {
    delete categories[name];
  });

  Object.keys(categories).forEach(name => {
    categories[name] = categories[name].map(item =>
      Object.assign({}, item, { category: item.category || name, subject })
    );
  });

  const seen = new Set();
  const items = Object.values(categories)
    .flat()
    .filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

  return { categories, items };
};

const comparableName = name => (typeof name === 'string' ? name.trim().toLowerCase() : '');

/**
 * The item with this id, else the item with this name (ignoring case and surrounding spaces).
 * Older paid entries may carry an id the Wallet no longer uses, so the name is the fallback.
 *
 * @returns {Object|null}
 */
const findWalletItem = (items, idOrName) => {
  if (!Array.isArray(items) || !idOrName) return null;
  const byId = items.find(item => item.id === idOrName);
  if (byId) return byId;
  const wanted = comparableName(idOrName);
  if (!wanted) return null;
  return items.find(item => comparableName(item.name) === wanted) || null;
};

/**
 * Whether an item is verified through the identity provider: a paid document that is not checked
 * through contacts.
 */
const providerVerifiedItem = item =>
  !!item && Number(item.price) > 0 && !CONTACT_CATEGORIES.includes(item.category);

module.exports = {
  UNBUILT_CATEGORIES,
  WALLET_CATEGORIES,
  walletCatalogFor,
  findWalletItem,
  providerVerifiedItem,
};
